/**
 * A10A — compatibility-barrel seam. Style-table (TEXTSTYLE / DIMSTYLE /
 * MLEADERSTYLE) and undo/redo history schemas extracted verbatim from the
 * red `editor.ts` (behaviour-preserving move, no redesign). Every symbol
 * here is re-exported from `./editor` so no external import path changes
 * and every moved symbol keeps its EXACT original type identity (re-export,
 * not a redeclaration).
 */
import { z } from "zod";

export const textStyleEntrySchema = z.object({
  name: z.string(),
  fontName: z.string(),
  height: z.number(),
  widthFactor: z.number(),
});

export const dimensionStyleEntrySchema = z.object({
  name: z.string(),
  textStyleName: z.string(),
  textHeight: z.number(),
  arrowheadSize: z.number(),
  overallScale: z.number(),
  precision: z.number(),
});

export const multileaderStyleEntrySchema = z.object({
  name: z.string(),
  textStyleName: z.string(),
  textHeight: z.number(),
  arrowheadSize: z.number(),
  contentType: z.number(),
});

export const styleTablesShellSchema = z.object({
  textStyles: z
    .array(textStyleEntrySchema)
    .default([
      { name: "Standard", fontName: "txt", height: 0, widthFactor: 1 },
    ]),
  dimensionStyles: z.array(dimensionStyleEntrySchema).default([
    {
      name: "Standard",
      textStyleName: "Standard",
      textHeight: 2.5,
      arrowheadSize: 2.5,
      overallScale: 1,
      precision: 4,
    },
  ]),
  multileaderStyles: z.array(multileaderStyleEntrySchema).default([
    {
      name: "Standard",
      textStyleName: "Standard",
      textHeight: 2.5,
      arrowheadSize: 2.5,
      contentType: 2,
    },
  ]),
});

/**
 * Phase 4.1.a — undo/redo history entry. Stores the forward
 * command + its inverse, plus selection snapshots so undo / redo
 * restore the selection set the user had at commit time.
 *
 * The `forward` and `inverse` fields are `z.unknown()` rather than
 * the recursive `editorCommandSchema` to avoid a Zod circular
 * dependency (the entry lives inside `editorShellStateSchema`,
 * which is itself referenced by command-handler types). At runtime
 * the dispatcher casts back to `EditorCommand`.
 */
export const historyEntrySchema = z.object({
  id: z.string().min(1),
  forward: z.unknown(),
  inverse: z.unknown(),
  summary: z.string(),
  spaceId: z.string().min(1),
  selectionBefore: z.array(z.string().min(1)),
  selectionAfter: z.array(z.string().min(1)),
  timestamp: z.number().int(),
});
export type HistoryEntry = z.infer<typeof historyEntrySchema>;

/**
 * Phase 4.1.a — in-progress batch. Populated between
 * `history/begin-batch` and `history/commit-batch`; commits as a
 * single `HistoryEntry` whose `forward` / `inverse` are arrays of
 * commands so multi-step user actions (SCALELISTEDIT Reset, layer-
 * state restore) collapse into one Ctrl-Z entry.
 */
export const historyBatchSchema = z.object({
  label: z.string().min(1),
  forwards: z.array(z.unknown()),
  inverses: z.array(z.unknown()),
  selectionBefore: z.array(z.string().min(1)),
  spaceId: z.string().min(1),
  startedAt: z.number().int(),
  /** Nested-batch depth counter; flattens to outermost on commit. */
  depth: z.number().int().nonnegative(),
});
export type HistoryBatch = z.infer<typeof historyBatchSchema>;
